import React from 'react';
import type {PressEvent} from '@react-types/shared';

type OverrideProps<K, V> = Omit<K, keyof V> & V;

export const NAVIGATE_EVENT = `sail:navigate`;
export type NavigateHandler<E> = (href: string, e: E) => void | false;

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ActionOverrideProps {}

// It enables the href type to be overridden by the consumer
export type Href = OverrideProps<{href?: string}, ActionOverrideProps>['href'];

type HrefAdapterHandler = (href: Href) => string | undefined;

type ActionNavigateContext = () => NavigateHandler<PressEvent>;
type ActionHrefAdapterContext = () => HrefAdapterHandler;

// default useNavigate context hook
function useDefaultNavigate(): NavigateHandler<PressEvent> {
  const navigateHandler = (_href: string, e: PressEvent): void | false => {
    const navigateEvent = new CustomEvent(NAVIGATE_EVENT, {
      bubbles: true,
      cancelable: true,
    });

    if (!e.target.dispatchEvent(navigateEvent)) {
      return false;
    }
  };

  return navigateHandler;
}

// default useHrefAdapter context hook
function useDefaultHrefAdapter() {
  const defaultHrefAdapter = (href: Href): string | undefined => {
    if (typeof href === 'undefined') {
      return undefined;
    }

    if (typeof href !== 'string') {
      throw new Error(
        'Invalid href value, please provide a correct hrefAdapter through the Unstable_ActionHrefAdapterContext',
      );
    }

    return href;
  };

  return defaultHrefAdapter;
}

export const Unstable_ActionHrefAdapterContext =
  React.createContext<ActionHrefAdapterContext>(useDefaultHrefAdapter);

export const Unstable_ActionNavigateContext =
  React.createContext<ActionNavigateContext>(useDefaultNavigate);
